/**
 * 实时数据面板：
 * - 必打论据覆盖度（manualEvidenceChecks，Phase 1 由手动勾选驱动）；
 * - P2P 连接与媒体状态（peering）；
 * - VAD 模式与语速/停顿/抢话统计（Phase 2 ASR 插槽未启用时显示提示）。
 */
import type { Side } from '@zongheng/core-schema';
import { sideLabel } from '@zongheng/core-schema';
import type { SpeechFrameStats, VadEngineMode } from '../../engines/contracts.ts';
import type { PeeringState } from '../../state/types.ts';
import { Badge, cx } from '../ui/primitives.tsx';
import { formatDuration, percent } from '../common/format.ts';

const VAD_MODE_LABEL: Record<VadEngineMode, string> = {
  silero: 'Silero VAD v5 · 端侧',
  energy: '能量阈值降级',
  unavailable: '未启用采集',
};

const VAD_MODE_TONE: Record<VadEngineMode, 'pass' | 'warning' | 'neutral'> = {
  silero: 'pass',
  energy: 'warning',
  unavailable: 'neutral',
};

export function LiveTelemetry({
  selfSide,
  evidenceIds,
  manualEvidenceChecks,
  peering,
  vadMode,
  speechStats,
}: {
  selfSide: Side;
  /** 当前底牌的必打论据 id 列表 */
  evidenceIds: string[];
  manualEvidenceChecks: Record<Side, string[]>;
  peering: PeeringState;
  vadMode: VadEngineMode;
  speechStats: SpeechFrameStats[];
}) {
  const myChecked = manualEvidenceChecks[selfSide] ?? [];
  const hitCount = evidenceIds.filter((id) => myChecked.includes(id)).length;
  const coverage = percent(hitCount, evidenceIds.length);

  return (
    <section
      data-testid="hud-telemetry"
      aria-label="实时数据"
      className="space-y-3 rounded-xl border border-white/10 bg-bg-surface/80 p-3 backdrop-blur"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold tracking-wide text-slate-100">实时数据</h2>
        <Badge tone="neutral">{sideLabel(selfSide)}视角</Badge>
      </div>

      <div>
        <div className="flex items-center justify-between text-[11px] text-slate-400">
          <span>必打论据覆盖度</span>
          <span className="mono">
            {hitCount}/{evidenceIds.length} · {coverage}%
          </span>
        </div>
        <div
          data-testid="hud-evidence-coverage"
          data-coverage={coverage}
          className="mt-1 h-2 overflow-hidden rounded-full bg-black/40"
          role="progressbar"
          aria-valuenow={coverage}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className={cx(
              'h-full rounded-full transition-all',
              coverage >= 100 ? 'bg-assert-pass' : coverage > 0 ? 'bg-team-aff' : 'bg-slate-700',
            )}
            style={{ width: `${coverage}%` }}
          />
        </div>
        {evidenceIds.length === 0 && (
          <p className="mt-1 text-[10px] text-slate-600">本环节无必打论据</p>
        )}
      </div>

      <div className="rounded-lg border border-white/10 bg-black/20 px-2.5 py-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wider text-slate-500">P2P 连接</span>
          <Badge tone={peering.connectedPeers > 0 ? 'pass' : 'neutral'}>
            {peering.connectedPeers}/{peering.totalPeers || '—'} 节点
          </Badge>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <Badge tone={peering.micEnabled ? 'pass' : 'neutral'}>麦克风 {peering.micEnabled ? '开' : '关'}</Badge>
          <Badge tone={peering.cameraEnabled ? 'pass' : 'neutral'}>摄像头 {peering.cameraEnabled ? '开' : '关'}</Badge>
          <Badge tone={peering.mediaEnabled ? 'pass' : 'warning'}>
            <span data-testid="hud-media-status">
              {peering.mediaEnabled ? '媒体已建立' : '媒体待启用'}
            </span>
          </Badge>
        </div>
        <p className="mt-1 text-[10px] leading-relaxed text-slate-500">{peering.note}</p>
      </div>

      <div className="rounded-lg border border-white/10 bg-black/20 px-2.5 py-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wider text-slate-500">
            VAD 语速 / 停顿 / 抢话
          </span>
          <span data-testid="hud-vad-mode">
            <Badge tone={VAD_MODE_TONE[vadMode]}>{VAD_MODE_LABEL[vadMode]}</Badge>
          </span>
        </div>
        {speechStats.length === 0 ? (
          <p className="mt-1 text-[10px] leading-relaxed text-slate-500">
            启用麦克风后，端侧 Silero VAD v5 将统计语速、停顿与抢话次数；模型/权限不可用时自动降级为能量 VAD，不影响训练流程。
          </p>
        ) : (
          <ul className="mt-1 space-y-1">
            {speechStats.map((stats) => (
              <li key={stats.side} className="flex items-center justify-between text-[11px] text-slate-400">
                <span>{sideLabel(stats.side)}</span>
                <span className="mono">
                  发言 {formatDuration(stats.speakingMs)} · 停顿 {stats.pauseCount} · 抢话{' '}
                  {stats.interruptionCount}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
